/**
 * CellMap - OpenLayers map wrapper for MeCell Maps
 *
 * Handles map initialization, tile rendering, and view management.
 */

import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { Projection } from 'ol/proj';
import TileGrid from 'ol/tilegrid/TileGrid';
import { defaults as defaultControls, FullScreen } from 'ol/control';
import { easeOut } from 'ol/easing';

import {
  MAP_EXTENT,
  RESOLUTIONS,
  TILE_SIZE,
  TILE_URL,
  INITIAL_CENTER,
  INITIAL_RESOLUTION,
  MIN_RESOLUTION,
  MAX_RESOLUTION,
  ANIMATION_DURATION
} from './config.ts';
import { StructureLayer } from './StructureLayer.js';
import { ClickHandler } from './ClickHandler.js';
import { KebLayer } from './KebLayer.js';

/**
 * Custom pixel-based projection for the cell map
 * This is NOT a geographic projection - we use raw pixel coordinates
 */
const projection = new Projection({
  code: 'PIXELS',
  units: 'pixels',
  extent: MAP_EXTENT
});

/**
 * Tile grid configuration matching our pre-rendered tiles
 */
const tileGrid = new TileGrid({
  extent: MAP_EXTENT,
  resolutions: RESOLUTIONS,
  tileSize: TILE_SIZE,
  origin: [0, 0] // Top-left origin
});

/**
 * CellMap class - wrapper around OpenLayers Map
 */
export class CellMap {
  /**
   * Create a new CellMap instance
   * @param {string|HTMLElement} target - DOM element or ID for map container
   */
  constructor(target) {
    // Structure layer (initialized separately via initStructures())
    this.structureLayer = null;

    // KEB layer (initialized separately via initKebs())
    this.kebLayer = null;

    // Click handler (initialized after structures via initClickHandler())
    this.clickHandler = null;

    // Create tile layer
    this.tileLayer = new TileLayer({
      source: new XYZ({
        url: TILE_URL,
        projection: projection,
        tileGrid: tileGrid,
        wrapX: false,
        crossOrigin: 'anonymous'
      }),
      preload: 2 // Preload adjacent tiles for smoother experience
    });

    // Create map view
    this.view = new View({
      projection: projection,
      center: INITIAL_CENTER,
      resolution: INITIAL_RESOLUTION,
      minResolution: MIN_RESOLUTION,
      maxResolution: MAX_RESOLUTION,
      extent: MAP_EXTENT, // Constrain panning to map bounds
      constrainOnlyCenter: false,
      smoothExtentConstraint: true,
      smoothResolutionConstraint: true
    });

    // Create map instance
    this.map = new Map({
      target: target,
      layers: [this.tileLayer],
      view: this.view,
      controls: defaultControls({
        attribution: false,
        zoom: true,
        rotate: false
      }).extend([
        new FullScreen({
          tipLabel: 'Vollbildmodus umschalten'
        })
      ])
    });

    // Handle window resize
    this._setupResizeHandler();

    // Debug logging in development
    if (import.meta.env.DEV) {
      this._setupDebugLogging();
    }
  }

  /**
   * Get the underlying OpenLayers Map instance
   * @returns {Map}
   */
  getMap() {
    return this.map;
  }

  /**
   * Get the map view
   * @returns {View}
   */
  getView() {
    return this.view;
  }

  /**
   * Get the tile layer
   * @returns {TileLayer}
   */
  getTileLayer() {
    return this.tileLayer;
  }

  /**
   * Get current map center
   * @returns {number[]} [x, y] coordinates
   */
  getCenter() {
    return this.view.getCenter();
  }

  /**
   * Get current zoom resolution
   * @returns {number} Current resolution
   */
  getResolution() {
    return this.view.getResolution();
  }

  /**
   * Get current zoom level (0-7)
   * @returns {number} Zoom level
   */
  getZoom() {
    return this.view.getZoom();
  }

  /**
   * Zoom to specific coordinates with animation
   * @param {number} x - X coordinate
   * @param {number} y - Y coordinate
   * @param {number} [resolution] - Target resolution (optional)
   * @param {Object} [options] - Animation options
   * @param {number} [options.duration] - Animation duration in ms
   * @param {boolean} [options.animate] - Whether to animate (default: true)
   * @returns {Promise<boolean>} Resolves when animation completes (true if completed, false if interrupted)
   */
  zoomTo(x, y, resolution, options = {}) {
    const {
      duration = ANIMATION_DURATION,
      animate = true
    } = options;

    const currentCenter = this.view.getCenter();
    const currentResolution = this.view.getResolution();

    // Skip if already at target (within tolerance)
    if (
      Math.abs(currentCenter[0] - x) < 1 &&
      Math.abs(currentCenter[1] - y) < 1 &&
      (resolution === undefined || Math.abs(currentResolution - resolution) < 0.01)
    ) {
      return Promise.resolve(true);
    }

    // Build animation options
    const animationOptions = {
      center: [x, y],
      duration: animate ? duration : 0,
      easing: easeOut
    };

    if (resolution !== undefined) {
      animationOptions.resolution = resolution;
    }

    // If not animating, just set values directly
    if (!animate) {
      this.view.setCenter([x, y]);
      if (resolution !== undefined) {
        this.view.setResolution(resolution);
      }
      return Promise.resolve(true);
    }

    // Return promise that resolves when animation completes
    return new Promise((resolve) => {
      this.view.animate(animationOptions, (complete) => {
        resolve(complete);
      });
    });
  }

  /**
   * Zoom to a structure by its data
   * @param {Object} structure - Structure object with position and resolution
   * @param {Object} [options] - Animation options
   * @returns {Promise<boolean>} Resolves when animation completes
   */
  zoomToStructure(structure, options = {}) {
    if (!structure || !structure.position) {
      console.warn('CellMap: Invalid structure for zoom:', structure);
      return Promise.resolve(false);
    }

    return this.zoomTo(
      structure.position.x,
      structure.position.y,
      structure.resolution,
      options
    );
  }

  /**
   * Zoom to a specific resolution (zoom level)
   * @param {number} resolution - Target resolution
   * @param {Object} [options] - Animation options
   * @returns {Promise<boolean>} Resolves when animation completes
   */
  setResolution(resolution, options = {}) {
    const { duration = ANIMATION_DURATION, animate = true } = options;

    if (!animate) {
      this.view.setResolution(resolution);
      return Promise.resolve(true);
    }

    return new Promise((resolve) => {
      this.view.animate(
        {
          resolution: resolution,
          duration: duration,
          easing: easeOut
        },
        (complete) => resolve(complete)
      );
    });
  }

  /**
   * Fit the view to the full extent
   * @param {Object} [options] - Animation options
   * @returns {Promise<boolean>} Resolves when animation completes
   */
  fitToExtent(options = {}) {
    const { duration = ANIMATION_DURATION, padding = [10, 10, 10, 10] } = options;

    return new Promise((resolve) => {
      this.view.fit(MAP_EXTENT, {
        duration: duration,
        padding: padding,
        easing: easeOut,
        callback: (complete) => resolve(complete)
      });
    });
  }

  /**
   * Reset view to initial state
   * @param {Object} [options] - Animation options
   * @returns {Promise<boolean>} Resolves when animation completes
   */
  resetView(options = {}) {
    const { duration = ANIMATION_DURATION, animate = true } = options;

    return this.zoomTo(INITIAL_CENTER[0], INITIAL_CENTER[1], INITIAL_RESOLUTION, {
      duration,
      animate
    });
  }

  /**
   * Add a layer to the map
   * @param {import('ol/layer/Layer').default} layer - Layer to add
   */
  addLayer(layer) {
    this.map.addLayer(layer);
  }

  /**
   * Remove a layer from the map
   * @param {import('ol/layer/Layer').default} layer - Layer to remove
   */
  removeLayer(layer) {
    this.map.removeLayer(layer);
  }

  /**
   * Register an event listener on the map
   * @param {string} type - Event type (e.g., 'click', 'pointermove')
   * @param {Function} listener - Event handler function
   * @returns {import('ol/events').EventsKey} Event key for removal
   */
  on(type, listener) {
    return this.map.on(type, listener);
  }

  /**
   * Unregister an event listener
   * @param {import('ol/events').EventsKey} key - Event key from on()
   */
  un(key) {
    this.map.un(key);
  }

  /**
   * Force map to update its size (call after container resize)
   */
  updateSize() {
    this.map.updateSize();
  }

  /**
   * Initialize the structure layer with polygon hit detection
   * @returns {Promise<StructureLayer>} The initialized structure layer
   */
  async initStructures() {
    this.structureLayer = new StructureLayer(this);
    await this.structureLayer.loadStructures();
    return this.structureLayer;
  }

  /**
   * Get the structure layer
   * @returns {StructureLayer|null}
   */
  getStructureLayer() {
    return this.structureLayer;
  }

  /**
   * Initialize the KEB layer with markers
   * @returns {Promise<KebLayer>} The initialized KEB layer
   */
  async initKebs() {
    this.kebLayer = new KebLayer(this);
    await this.kebLayer.loadKebs();
    return this.kebLayer;
  }

  /**
   * Get the KEB layer
   * @returns {KebLayer|null}
   */
  getKebLayer() {
    return this.kebLayer;
  }

  /**
   * Initialize click handling (call after structures loaded)
   * @returns {ClickHandler}
   */
  initClickHandler() {
    if (!this.structureLayer) {
      throw new Error('Structures must be loaded before click handler');
    }
    this.clickHandler = new ClickHandler(this, this.structureLayer, this.kebLayer);
    return this.clickHandler;
  }

  /**
   * Get click handler
   * @returns {ClickHandler|null}
   */
  getClickHandler() {
    return this.clickHandler;
  }

  /**
   * Subscribe to structure selection events
   * @param {Function} callback - Function to call with selected structure data
   * @returns {Function} Unsubscribe function
   */
  onStructureSelect(callback) {
    if (!this.clickHandler) {
      throw new Error('Click handler not initialized');
    }
    return this.clickHandler.on('structure-select', callback);
  }

  /**
   * Subscribe to structure deselection events
   * @param {Function} callback - Function to call on deselection
   * @returns {Function} Unsubscribe function
   */
  onStructureDeselect(callback) {
    if (!this.clickHandler) {
      throw new Error('Click handler not initialized');
    }
    return this.clickHandler.on('structure-deselect', callback);
  }

  /**
   * Subscribe to KEB selection events
   * @param {Function} callback - Function to call with { keb, coordinate }
   * @returns {Function} Unsubscribe function
   */
  onKebSelect(callback) {
    if (!this.clickHandler) {
      throw new Error('Click handler not initialized');
    }
    return this.clickHandler.onKeb('keb-select', callback);
  }

  /**
   * Destroy the map and clean up resources
   */
  destroy() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
    }
    this.map.setTarget(null);
  }

  /**
   * Set up resize handler to update map on container size change
   * @private
   */
  _setupResizeHandler() {
    // Use ResizeObserver if available for better performance
    if (typeof ResizeObserver !== 'undefined') {
      const container = this.map.getTargetElement();
      if (container) {
        this._resizeObserver = new ResizeObserver(() => {
          this.map.updateSize();
        });
        this._resizeObserver.observe(container);
      }
    } else {
      // Fallback to window resize event
      window.addEventListener('resize', () => {
        this.map.updateSize();
      });
    }
  }

  /**
   * Set up debug logging for development
   * @private
   */
  _setupDebugLogging() {
    this.map.on('moveend', () => {
      const center = this.getCenter();
      const resolution = this.getResolution();
      const zoom = this.getZoom();
      console.info(`Map: center=[${center[0].toFixed(0)}, ${center[1].toFixed(0)}], resolution=${resolution.toFixed(2)}, zoom=${zoom?.toFixed(1) || 'N/A'}`);
    });
  }
}

/**
 * Factory function to create a CellMap instance
 * @param {string|HTMLElement} target - DOM element or ID for map container
 * @returns {CellMap}
 */
export function createCellMap(target) {
  return new CellMap(target);
}
